
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Button, Popover } from 'ant-design-vue';
import {
    determineIfProductCategoryMustBeSimple,
    transformProducts,
    transformProductSystems,
} from '@/helpers/Products/ProductHelper';
import ProductCategoriesRepository from '@/repositories/ProductCategoriesRepository';
import { RouteNames } from '@/enums/routes/RouteNames';
import MultiplierModal from '@/components/views/NewProjectView/Offer/MultiplierModal.vue';
import { MultipositionPopupSettings } from '@/interfaces/components/configurator/MultipositionPopupSettings';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { PopupEvents } from '@/enums/global/PopupEvents';
import { fetchAndRegisterJsonLogicFunctionsIfNecessary } from '@/helpers/JsonLogicHelper';
import DimensionsWizard from '@/components/global/popup/DimensionsWizard.vue';
import ConfiguratorService from '@/services/ConfiguratorService';

@Component({
    name: 'ProductSelector',
    components: { Popover, Button, MultiplierModal, DimensionsWizard },
})
export default class ProductSelector extends Vue {
    @Prop({ default: false }) private shouldPickerBeLocked!: boolean;
    @Prop({ required: true }) private productCategoryId!: string;
    @Prop({ default: null }) private clientId!: string | null;
    @Prop({ default: null }) private clientsPaymentTypeId!: string | null;
    @Prop({ default: null }) private selectedOfferId!: string | null;
    @Prop({ required: true }) private projectId!: string;

    private multipositionSettings: null | MultipositionPopupSettings = null;
    private timesToMultiplyMultipositionProduct = 1;
    private isMultiplierModalVisible = false;
    private openPopovers: any = {};

    private get productsByCategoryId() {
        if (this.productCategoryId === null) {
            return [];
        }

        const categoryById = ProductCategoriesRepository.getById(this.productCategoryId);
        if (categoryById == null) {
            return [];
        }

        return categoryById.products;
    }

    private get itemsToDisplay() {
        if (determineIfProductCategoryMustBeSimple(this.productCategoryId)) {
            return transformProductSystems(this.productSystems);
        }

        return transformProducts(this.productsByCategoryId);
    }

    private get productSystems() {
        return this.productsByCategoryId
            .map((product) => {
                return product.productSystems;
            })
            .flat();
    }

    private hidePopover(i: number) {
        this.$set(this.openPopovers, i, false);
    }

    private shouldShowDimensionsWizard(shouldShowDimensionsWizard: boolean) {
        return shouldShowDimensionsWizard && (this.productCategoryId === '5' || this.productCategoryId === '6');
    }

    private onProductClick(item: any, i: number) {
        this.hidePopover(i);

        const params = {
            productFormId: item.productFormId,
            productSystemId: item.productSystemId,
            productId: item.productId,
        };

        this.$router.push({
            name: RouteNames.configurator,
            // @ts-ignore
            params,
            query: {
                projectId: this.projectId,
                offerId: this.selectedOfferId,
                clientId: this.clientId,
                productSystemName: item.isProductCustom ? null : item.productSystemCode,
            },
        });
    }

    private async openMultiplierPopup(item: any, multipositionType: string, i: number) {
        this.hidePopover(i);
        const contextKey = `ADD_ITEMS_FLOW`;
        const offerId = this.selectedOfferId;

        this.multipositionSettings = {
            productFormId: item.productFormId,
            productId: item.productId,
            productSystemName: item.isProductCustom ? null : item.productSystemCode,
            productName: item.productName,
            productSystemId: item.productSystemId,
            multipositionType,
            isCombinations: false,
            contextKey: contextKey,
        };

        const draft = offerId ? ConfiguratorService.getLocalDraft(offerId, contextKey) : null;

        if (draft) {
            this.$confirm({
                title: this.$t('Restore unsaved draft?') as string,
                content: this.$t('You have unsaved changes from your last session.') as string,
                okText: this.$t('Restore') as string,
                cancelText: this.$t('Discard and start new') as string,
                onOk: () => {
                    this.openMultipositionPopup(draft, true);
                },
                onCancel: () => {
                    if (offerId) {
                        ConfiguratorService.clearLocalDraft(offerId, contextKey);
                    }
                    this.isMultiplierModalVisible = true;
                },
            });
        } else {
            // No draft: Proceed to ask for the number of products
            this.isMultiplierModalVisible = true;
        }
    }
    

    // Update this method to accept optional draft data
    private async openMultipositionPopup(restoredDraft: any = null, isRestoring = false) {
        await fetchAndRegisterJsonLogicFunctionsIfNecessary();

        // Prepare the final data object
        const popupData = isRestoring 
            ? { ...restoredDraft, isRestoringDraft: true }
            : { 
                ...this.multipositionSettings, 
                timesToMultiply: this.timesToMultiplyMultipositionProduct,
                isRestoringDraft: false,
                clientId: this.clientId,
                clientsPaymentTypeId: this.clientsPaymentTypeId,
                offerId: this.selectedOfferId,
                projectId: this.projectId,
            };

        EventBus.$emit(EventBusEvents.openAddProductsPopup, {
            popupEvent: PopupEvents.openAddProducts,
            data: popupData,
        });

        // Reset local state
        this.isMultiplierModalVisible = false;
        this.multipositionSettings = null;
        this.timesToMultiplyMultipositionProduct = 1;
    }

    private openDimensionsWizard(item: any, i: number) {
        this.hidePopover(i);

        const wizardData = {
            productFormId: item.productFormId,
            productSystemId: item.productSystemId,
            productId: item.productId,
            projectId: this.projectId,
            offerId: this.selectedOfferId,
            clientId: this.clientId,
            productSystemName: item.isProductCustom ? null : item.productSystemCode,
            productName: item.productName,
            clientsPaymentTypeId: this.clientsPaymentTypeId,
            customFieldValues: [{ pId: 'p740593', value: 'Dimenzije oken' }],
            title: 'Wizard for window dimensions',
        };

        EventBus.$emit(EventBusEvents.openDimensionsWizard, {
            popupEvent: PopupEvents.openDimensionsWizard,
            data: wizardData,
        });
    }

    private async openCombinationsWizardPopup(item: any, i: number) {
        this.hidePopover(i);

        const wizardData = {
            productFormId: item.productFormId,
            productSystemId: item.productSystemId,
            productId: item.productId,
            projectId: this.projectId,
            offerId: this.selectedOfferId,
            clientId: this.clientId,
            productSystemName: item.isProductCustom ? null : item.productSystemCode,
            productName: item.productName,
            clientsPaymentTypeId: this.clientsPaymentTypeId,
            title: 'Wizard for window combinations',
        };

        EventBus.$emit(EventBusEvents.openCombinationsWizard, {
            popupEvent: PopupEvents.openCombinationsWizard,
            data: wizardData,
        });
    }

    // @Watch('multipositionSettings')
    // private onMultiplierSettingsChange() {
    //     this.isMultiplierModalVisible = this.multipositionSettings != null;
    // }
}
